import {
	ISkeletonLoader,
	ISkeletonLoaderBreakpoint,
	ISkeletonLoaderVariable,
} from '@kurtosys/types/appsManager/index.js';

import { commonUtils } from '../common/index.js';

export const DEFAULT_SKELETON_LOADER_TAG = 'div';

export class SkeletonLoader {
	constructor(public config: ISkeletonLoader, public globalVariables: ISkeletonLoaderVariable[] = []) {}
	render(targetElement: HTMLElement): void {
		if (targetElement) {
			this.renderElement(targetElement);
			this.renderStyle();
		}
	}
	renderElement(targetElement: HTMLElement): void {
		const element = this.getElement();
		targetElement.appendChild(element);
	}
	renderStyle(): void {
		const css = this.getSkeletonCss(true);
		if (css) {
			const styleElement = document.createElement('style');
			styleElement.innerHTML = css;
			document.head.appendChild(styleElement);
		}
	}
	get elementId(): string {
		return `ksys-skeleton-${this.config.id}-${this.configurationKey}-${this.styleKey}`;
	}
	get configurationKey(): string {
		return this.config.configurationKey || 'default';
	}
	get styleKey(): string {
		return this.config.styleKey || 'default';
	}
	getElement(): HTMLElement {
		const { tag = DEFAULT_SKELETON_LOADER_TAG } = this.config;
		const element = document.createElement(tag);
		element.id = this.elementId;
		return element;
	}
	getValue(keyOrValue: string, variablesByKey: { [key: string]: ISkeletonLoaderVariable }): string {
		return commonUtils.replacePlaceholders({ values: variablesByKey, text: keyOrValue });
	}
	getSkeletonCss(wrapCssWithId = false): string {
		const { breakpoints = [] } = this.config;
		const response: string[] = [];
		for (const breakpoint of breakpoints) {
			const { maxWidthInPixels, minWidthInPixels } = breakpoint;
			const css = this.getSkeletonInnerCss(breakpoint);
			const innerCss = [css];
			if (wrapCssWithId) {
				innerCss.unshift(`#${this.elementId} {`);
				innerCss.push('}');
			}
			if (!commonUtils.isNullOrUndefined(maxWidthInPixels) || !commonUtils.isNullOrUndefined(minWidthInPixels)) {
				let key: string | null = null;
				if (!commonUtils.isNullOrUndefined(maxWidthInPixels)) {
					key = 'max';
				} else if (!commonUtils.isNullOrUndefined(minWidthInPixels)) {
					key = 'min';
				}
				if (key) {
					const value = !commonUtils.isNullOrUndefined(maxWidthInPixels)
						? maxWidthInPixels
						: minWidthInPixels;
					const defaultMedia = ['print', 'screen'];
					const media = defaultMedia;
					response.push(`@media ${media.join(', ')} and (${key}-width: ${value}px) {`);
					response.push(...innerCss);
					response.push('}');
				}
			} else {
				response.push(...innerCss);
			}
		}
		return response.join('\n');
	}
	getSkeletonInnerCss(breakpoint: ISkeletonLoaderBreakpoint): string {
		const image: string[] = [];
		const position: string[] = [];
		const size: string[] = [];

		const { children = [], variables = [], style } = breakpoint;
		const variablesByKey = commonUtils.pivotCollection(
			[...this.globalVariables, ...variables],
			(variable): string => variable.key,
			(variable): string => variable.value,
		);
		for (const child of children) {
			const { fill, x, y, width, height } = child;
			image.push(
				`linear-gradient(${this.getValue(fill, variablesByKey)} ${this.getValue(
					height,
					variablesByKey,
				)}, transparent 0)`,
			);
			position.push(`${this.getValue(x, variablesByKey)} ${this.getValue(y, variablesByKey)}`);
			size.push(`${this.getValue(width, variablesByKey)} ${this.getValue(height, variablesByKey)}`);
		}
		const componentStyle = new commonUtils.ComponentStyle({});
		const css = `
	${style ? componentStyle.getCss(style) : ''}
	background-repeat: no-repeat;
	background-image: ${image.join(',')};
	background-position: ${position.join(',')};
	background-size: ${size.join(',')};
			`;

		return css;
	}
}
